/** The namespaces the metric registry prefixes its keys with. */
export const CONNECTOR_METRIC_NAMESPACES = [
  'qbo', 'quickbooks', 'hubspot', 'crm', 'salesforce', 'github', 'jira',
  'slack', 'stripe', 'web', 'website',
  'doc',
] as const;

const DOCUMENT_SOURCE_IDS = new Set(['data_room', 'data_room_csv', 'upload', 'document']);
const DOCUMENT_SOURCE_FAMILIES = new Set(['layer2_data_room']);

const CONNECTOR_LABELS: Record<string, string> = {
  quickbooks: 'QuickBooks',
  qbo: 'QuickBooks',
  hubspot: 'HubSpot',
  crm: 'the connected CRM',
  salesforce: 'Salesforce',
  github: 'GitHub',
  jira: 'Jira',
  slack: 'Slack',
  stripe: 'Stripe',
  web: "the company's website",
  website: "the company's website",
  diagnostic: 'a previous Read',
  // Company-read sources: what the company supplied, or what was read about it.
  company_document: 'Company file (self-reported)',
  company_website: "the company's website",
  third_party: 'a third-party source',
};

/** The standing phrase for a figure a company stated in a file of its own. */
export const COMPANY_FILE_SOURCE_LABEL = 'Company file (self-reported)';

const namespacePattern = new RegExp(`^(${CONNECTOR_METRIC_NAMESPACES.join('|')})\\.`, 'i');

export function hasConnectorNamespace(metricKey: string): boolean {
  return namespacePattern.test(metricKey ?? '');
}

export function stripConnectorNamespace(metricKey: string): string {
  return (metricKey ?? '').replace(namespacePattern, '');
}

/**
 * What a customer calls each reading the registry knows, by its key. One list for every surface:
 * the evidence appendix, Said vs true, the findings, the PDF and the exports (ZPM-428).
 * apps/api/src/lib/evidence-presentation.ts holds the same list; an API test pins the two together.
 */
export const READING_NAMES: Readonly<Record<string, string>> = {
  'qbo.gross_margin_pct': 'Gross margin',
  'qbo.burn_monthly': 'Monthly cash burn',
  'qbo.burn_multiple': 'Burn multiple',
  'qbo.cash_runway_months': 'Cash runway',
  'qbo.cash_balance': 'Cash balance',
  'qbo.opex_monthly': 'Monthly operating expenses',
  'qbo.cash_generation_monthly': 'Monthly cash generation',
  'qbo.period_revenue': 'Period revenue',
  'qbo.monthly_revenue': 'Monthly revenue',
  'qbo.revenue_concentration_top1_pct': 'Largest customer share of revenue',
  'qbo.revenue_concentration_top5_pct': 'Top-five revenue concentration',
  'qbo.revenue_customer_count': 'Customers with revenue',
  'qbo.revenue_customer_coverage_pct': 'Revenue attributed to a customer',
  'hubspot.win_rate_90d': 'Win rate',
  'hubspot.cycle_time_days': 'Sales cycle',
  'hubspot.pipeline_value_open': 'Open pipeline value',
  'hubspot.open_deals_count': 'Open deals',
  'hubspot.new_arr_90d': 'New ARR',
  'hubspot.won_deal_value_90d': 'Closed-won deal value',
  'hubspot.nrr': 'Net revenue retention',
  'hubspot.ltv': 'Customer lifetime value',
  'hubspot.cac_payback_months': 'CAC payback period',
  'hubspot.csat_score': 'CSAT score',
  'hubspot.sales_velocity_90d': 'Sales velocity',
  'hubspot.nrr_deal_count': 'Deals carrying net revenue retention',
  'hubspot.ltv_deal_count': 'Deals carrying lifetime value',
  'hubspot.cac_payback_deal_count': 'Deals carrying CAC payback',
  'hubspot.csat_deal_count': 'Deals carrying CSAT',
  'github.commit_freq_weekly': 'Weekly commits',
  'github.pr_cycle_time_h': 'Pull request open-to-merge time',
  'github.pr_time_to_first_review_h': 'Time to first pull request review',
  'github.deploy_freq_weekly': 'Weekly deployments',
  'github.repos_scanned': 'Repositories sampled',
  'jira.on_time_delivery_rate_pct': 'On-time delivery rate',
  'jira.cycle_time_days': 'Issue cycle time',
  'jira.throughput_weekly': 'Weekly issue throughput',
  'jira.issues_scanned': 'Issues sampled',
  'slack.after_hours_msg_ratio': 'After-hours message share',
  'slack.sentiment_score_30d': 'Message sentiment indicator',
  'slack.weekly_sentiment_score': 'Weekly message sentiment indicator',
  'slack.leadership_alignment_index': 'Leadership language indicator',
  'slack.psych_safety_proxy': 'Message safety-language indicator',
  'slack.messages_sampled': 'Messages sampled',
  'slack.thread_response_latency_h': 'Thread response time',
  'slack.decision_cadence_threads_weekly': 'Weekly answered threads',
  'web.public_pages_scanned': 'Public pages read',
  'web.positioning_clarity_score': 'Page-text positioning heuristic',
  'web.moat_signal_count': 'Moat keyword matches on the fetched page',
  'hr.voluntary_attrition_rate_pct': 'Voluntary attrition rate',
  'hr.executive_turnover_rate_pct': 'Executive turnover rate',
  'hr.headcount': 'Headcount',
  'hr.executive_headcount': 'Executive headcount',
  'hr.median_tenure_months': 'Median tenure',
  'hr.span_of_control': 'Span of control',
  'hr.senior_hire_ratio_pct': 'Senior hire share',
  'hr.census_coverage_pct': 'HR census coverage',
  // Document readings (HRD-1-S02 AC4): a figure stated in an uploaded file, named like the reading it stands in for.
  'doc.monthly_revenue': 'Monthly revenue',
  'doc.period_revenue': 'Period revenue',
  'doc.cash_runway_months': 'Cash runway',
  'doc.cash_balance': 'Cash balance',
  'doc.pipeline_value_open': 'Open pipeline value',
  'doc.burn_monthly': 'Monthly cash burn',
  'doc.gross_margin_pct': 'Gross margin',
  'doc.nrr': 'Net revenue retention',
  'doc.headcount': 'Headcount',
  'doc.deploy_freq_weekly': 'Weekly deployments',
  'doc.revenue_customer_count': 'Customers with revenue',
  'doc.positioning_clarity_score': 'Page-text positioning heuristic',
  'doc.moat_signal_count': 'Moat keyword matches on the fetched page',
};

/**
 * The spelling an older Read stored in place of the key: the namespace of five connectors dropped
 * and underscores turned to spaces ("gross margin pct", "jira.cycle time days").
 */
function storedEvidenceSpelling(key: string): string {
  return key.replace(/^(qbo|hubspot|github|slack|web)\./, '').replace(/_/g, ' ').toLowerCase();
}

const KEY_BY_STORED_SPELLING: ReadonlyMap<string, string> = (() => {
  const index = new Map<string, string>();
  const ambiguous = new Set<string>();
  // Document readings (`doc.*`) are newer than the spaced spelling, so an older Read's bare key
  // ("cash balance") always meant the connector key; a document key only claims its own spelling.
  const isDocumentKey = (key: string) => key.startsWith('doc.');
  for (const key of Object.keys(READING_NAMES)) {
    for (const spelling of [storedEvidenceSpelling(key), stripConnectorNamespace(key).toLowerCase()]) {
      if (spelling === key) continue;
      const prior = index.get(spelling);
      if (!prior || prior === key) index.set(spelling, key);
      else if (isDocumentKey(key)) continue;
      else if (isDocumentKey(prior)) index.set(spelling, key);
      else ambiguous.add(spelling);
    }
  }
  for (const spelling of ambiguous) index.delete(spelling);
  return index;
})();

/**
 * The registry key behind a stored reading key, whether it was stored as the key itself or in the
 * spaced spelling older Reads carry. Undefined when the registry does not know the reading.
 */
export function readingRegistryKey(metricKey: string | undefined): string | undefined {
  const raw = (metricKey ?? '').trim();
  if (!raw) return undefined;
  // Own keys only: "toString" or "constructor" is not a reading, and would hand back a function.
  if (Object.hasOwn(READING_NAMES, raw)) return raw;
  const lower = raw.toLowerCase();
  if (Object.hasOwn(READING_NAMES, lower)) return lower;
  return KEY_BY_STORED_SPELLING.get(lower) ?? KEY_BY_STORED_SPELLING.get(lower.replace(/ /g, '_'));
}

/** The name of the reading, for a customer. A supplied label wins; then the registry; then the key, tidied. */
export function customerReadingName(metricKey: string | undefined, label?: string): string {
  const trimmed = (label ?? '').trim();
  if (trimmed && !hasConnectorNamespace(trimmed)) return trimmed;
  const registryKey = readingRegistryKey(metricKey);
  if (registryKey) return READING_NAMES[registryKey]!;
  const bare = stripConnectorNamespace(metricKey ?? '').replace(/_/g, ' ').trim();
  if (!bare) return trimmed || 'Unnamed reading';
  return bare.charAt(0).toUpperCase() + bare.slice(1);
}

export interface ReadingSourceInput {
  source?: string;
  source_family?: string;
  document_title?: string;
  /** How the value was obtained, from the reading's own provenance. */
  method?: string;
}

export function isDocumentReading(input: ReadingSourceInput): boolean {
  const source = (input.source ?? '').toLowerCase();
  const family = (input.source_family ?? '').toLowerCase();
  const method = (input.method ?? '').toLowerCase();
  return DOCUMENT_SOURCE_IDS.has(source)
    || DOCUMENT_SOURCE_FAMILIES.has(family)
    || method === 'document_parser' || method === 'csv_parser';
}

/** Who says this figure, in the customer's words. Never a raw id. */
export function customerSourceLabel(input: ReadingSourceInput): string {
  if (isDocumentReading(input)) {
    const title = (input.document_title ?? '').trim();
    return title || COMPANY_FILE_SOURCE_LABEL;
  }
  const source = (input.source ?? '').toLowerCase();
  if (Object.hasOwn(CONNECTOR_LABELS, source)) return CONNECTOR_LABELS[source]!;
  const family = (input.source_family ?? '').toLowerCase();
  if (Object.hasOwn(CONNECTOR_LABELS, family)) return CONNECTOR_LABELS[family]!;
  return 'a connected system';
}

export interface ProvenanceInput extends ReadingSourceInput {
  locator?: string;
}

/** The "Provenance details" line, in plain words rather than a family id. */
export function customerProvenanceText(input: ProvenanceInput): string {
  const where = customerSourceLabel(input);
  const parts = isDocumentReading(input)
    ? [`Read out of ${where}, as the company stated it.`]
    : [`Measured in ${where}.`];
  if (input.locator?.trim()) parts.push(`Found at ${input.locator.trim()}.`);
  return parts.join(' ');
}
